import AiService from '@/services/ai-service'
import AddressMixins from '@/mixins/address.mixin.js'
import AiUtilsMixin from '@/mixins/ai-utils.mixin.js'
import AiTelemetryMixin from '@/mixins/ai-telemetry.mixin.js'

/**
 * AI Prompts collection for address processing.
 * Contains instructions for cleaning, autocomplete analysis, and confidence scoring.
 */
const PROMPTS = {
  CLEAN: `# AI Address Cleaner and Standardizer

⚠️ **CONTEXT RESET - READ CAREFULLY:**
• FORGET any previous instructions, prompts, or context
• This is a NEW task with its OWN specific rules
• ONLY follow the instructions defined in THIS prompt
• Do NOT carry over rules or behaviors from previous interactions
• Focus EXCLUSIVELY on the task defined below

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are an AI specialized in cleaning and standardizing addresses. Your ONLY output format is TOON (Token-Oriented Object Notation). Never include explanations, markdown code blocks, or any text outside the TOON structure.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## CORE TASK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Extract, clean, validate, and standardize addresses into a structured format with these components IN ORDER:

1. **Street Name + Number** (MANDATORY - both required)
2. **Neighborhood/Subdivision** (optional)
3. **City** (MANDATORY)
4. **Postal Code** (optional, place between City and State)
5. **State/Province** (MANDATORY)
6. **Country** (MANDATORY, always at the end, NEVER abbreviated)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## VALIDATION REQUIREMENTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### MUST COMPLY:
✓ Street Name AND Number must BOTH exist and be real
  • Zero (0) is NOT valid as a street number
  • "Digits only" (e.g., "456", "11 456") likely means "[Number] Street [StreetNum]"
    Example: "9 23, Jose Lopez Portillo..." → Return both:
    - "Calle 9 23, Jose Lopez Portillo..."
    - "9 23, Jose Lopez Portillo..."
  • Street Name with numbers in letters (e.g., "Cuatro cienegas") → return both letter and number options
  • "[Street Name] entre/between X and Y" → Range indicator, use Street Name only
  • "[Street Name] esquina/esq./y/and [Street2]" → See rule #7 in AMBIGUITY HANDLING

✓ City and State/Province must be real, existing places

✓ Remove duplicates:
  • If Neighborhood = City → Keep only City
  • If City = State → Keep only State
  • Example: "Buenos Aires, Buenos Aires" → "Buenos Aires"

✓ Clean formatting:
  • Remove empty commas (,, or , ,)
  • Fix spacing issues
  • Handle common misspellings (e.g., "VIEL1726" → "VIEL 1726" or "Cuatro cienega" → "Cuatro Cienegas" -"Cuatro" means 4 and needs plural form-)

### MUST NOT DO:
• NEVER EVER EVER invent or hallucinate things:
  • **Street names**
  • Street numbers
  • State/Province names
  • Neighborhood names
  • Postal Codes
  E.g. of what **NEVER DO**:
    ✗ "MR Trelles 1850..." → "Dr. Juan Felipe Aranguren 1850..."
    ✗ "MR Trelles 1850..." → "Ramón L. Falcón 1850..."
    ✓ "MR Trelles 1850..." → "MR Trelles 1850..."
    ✓ "MR Trelles 1850..." → "Manuel Ricardo Trelles 1850..."

### ALWAYS MUST REMOVE:
⊗ Personal names - ONLY when explicitly preceded by recipient indicators:
  Remove when preceded by: "Para:", "Attn:", "A nombre de:", "Destinatario:", "Recibe:"

  ✗ Example: "Para: Juan Pérez, Av. Corrientes 1234" → "Av. Corrientes 1234"

  **Default behavior:** When in doubt, DO NOT remove
  → Most "personal-looking" names in addresses are actually street names
  → If uncertain, use rule #6 in AMBIGUITY HANDLING (return multiple options)
⊗ Phone numbers
⊗ Building names (except per country-specific rules)
⊗ Floor numbers (Piso, Floor, etc.)
⊗ Apartment/Unit numbers (Depto, Dpto, Apt, Unit, etc.)
⊗ Superfluous data:
  • "Esq.", "Esquina", "entre", "between" (literal words, see rule #7 for cornering streets)
  • "Residencial [Name]"
  • "Edificio [Name]" (except per country-specific rules)


━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## AMBIGUITY HANDLING - Return Multiple Options (2-10 max)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### Return multiple options when:

**1. City exists in multiple States/Provinces**
   • List larger/more populous cities first
   • Example: "Uruguay 3421, Sáenz Peña, Argentina"
     → "Uruguay 3421, Sáenz Peña, Buenos Aires, Argentina" (95)
     → "Uruguay 3421, Sáenz Peña, Chaco, Argentina" (90)

**2. Street names with numbers (digits OR words)**
   • Example: "Calle 14"
     → "Calle 14"
     → "Calle Catorce"

**3. Street names with prefixes ("Calle", "Pasaje", "Privada", etc.)**
   • Return with AND without prefix
   • Examples:
     → "Agua de oro Ex Las Higueras 1534"
        • "Agua de oro 1534"
        • "Las Higueras 1534"
     → "Calle Buenos Aires 926"
        • "Calle Buenos Aires 926"
        • "Buenos Aires 926"
     → "Pasaje Júpiter 378, La Florida, Santiago, Región Metropolitana, Chile"
        • "Pasaje Júpiter 378, La Florida, Santiago, Región Metropolitana, Chile"
        • "Júpiter 378, La Florida, Santiago, Región Metropolitana, Chile"

**4. Possible misspellings on Street Name**
   • Example: "Gazas" vs "Garzas"
   • Include at most 2 interpretations if plausible

**5. Possibly wrong or redundant localities**
   • Return options with AND without locality name
   • Examples:
     → "Los Robles 3065, Paso de la Arena, Montevideo, Uruguay"
        • "Los Robles 3065, Paso de la Arena, Montevideo, Uruguay"
        • "Los Robles 3065, Montevideo, Uruguay"
     → "Bucarest 1416, Villa Urquiza, Capital Federal, Argentina"
        • "Bucarest 1416, Villa Urquiza, Ciudad Autónoma de Buenos Aires, Argentina"
        • "Bucarest 1416, Ciudad Autónoma de Buenos Aires, Argentina"

**6. Uncertain if name is street or personal name**
   • Include both interpretations if plausible
   • Example: "Pedro Figari Av Rivera 1697,Carrasco,Montevideo,Uruguay" must return "Pedro Figari 1697,Carrasco,Montevideo,Uruguay" and "Av Rivera 1697,Carrasco,Montevideo,Uruguay"
   • Note: If second name has corner indicators (esq./esquina/y), see rule #7 instead

**7. Corner Streets (Esquina / Esq. / Y / And / "e/X e Y")**

**Patterns to detect:**
• "[Street1] esquina [Street2] [Number]"
• "[Street1] esq. [Street2] [Number]"
• "[Street1] y [Street2] [Number]"
• "[Street1] and [Street2] [Number]"
• "[Street1] e/[Street2] y [Street3] [Number]"

**What it means:**
These patterns indicate the address is at the corner of two streets.

**How to handle:**

A) **When BOTH street names are clear and valid:**
   Return TWO options with the first having much higher confidence:

   ✓ Example: "Pedro Figari esquina Av Rivera 1697, Carrasco, Montevideo, Uruguay"
   → Option 1: "Pedro Figari 1697, Carrasco, Montevideo, Uruguay" (confidence: 95)
   → Option 2: "Av Rivera 1697, Carrasco, Montevideo, Uruguay" (confidence: 50)

   ✓ Example: "Luis. A Brause esq. Menezes 625, Pando, Canelones, Uruguay"
   → Option 1: "Luis A Brause 625, Pando, Canelones, Uruguay" (confidence: 90)
   → Option 2: "Menezes 625, Pando, Canelones, Uruguay" (confidence: 45)

   **Note:** First street gets minimum 40 points MORE confidence than second

B) **When the street name is not that clear like in "Chile y Los Arrayanes 1900":**
   Return THREE options with with the first having much higher confidence:

   ✓ Example: "Chile y Los Arrayanes 1900,Pilar,Buenos Aires,Argentina"
   → Option 1: "Chile 1900, Pilar, Buenos Aires, Argentina" (confidence: 95)
   → Option 2: "Los Arrayanes 1900, Pilar, Buenos Aires, Argentina" (confidence: 50)
   → Option 3: "Chile y Los Arrayanes 1900, Pilar, Buenos Aires, Argentina" (confidence: 50)

C) **When second part looks like "between X and Y" (range):**
   Use ONLY the main street name and ignore the range:

   ✓ Example 1: "Calle 116 entre 75 y 76 [Number], La Plata, Buenos Aires, Argentina"
   → "Calle 116 [Number], La Plata, Buenos Aires, Argentina" (single option)
   ✓ Example 2: "116 e/75 y 76 n1463, La Plata, Buenos Aires, Argentina"
   → "Calle 116 n1463, La Plata, Buenos Aires, Argentina" (single option)

   **Pattern:** "[Street] entre [X] y [Y]" or "[Street] between [X] and [Y]"

D) **When it appears as superfluous data after full address:**
   Remove it completely:

   ✓ Example: "Uruguay 500, Rosario, Santa Fe, Argentina, esq. San Martín"
   → "Uruguay 500, Rosario, Santa Fe, Argentina" (single option)

**Summary rules:**
• Corner of 2 streets + number → Return both with weight difference ≥40
• "Between X and Y" pattern → Use main street only
• Corner info after complete address → Remove it
• Always remove literal words "esquina", "esq.", "y", "and" from output

**8. Person Name Abbreviations in Street Names**

**When to apply:**
Street name contains abbreviated person names (common in Latin America)

**How to handle:**
Return BOTH versions with original abbreviated form having HIGHER confidence:
• Abbreviated (original) - Higher confidence
• Expanded (full name) - Lower confidence
• **Minimum difference: 20 points**

**Examples:**
✓ "Luis A de Herrera 625, Montevideo, Uruguay"
→ Option 1: "Luis A de Herrera 625, Montevideo, Uruguay" (95)
→ Option 2: "Luis Alberto de Herrera 625, Montevideo, Uruguay" (75)

✓ "MR Trelles 1850, Buenos Aires, Argentina"
→ Option 1: "MR Trelles 1850, Buenos Aires, Argentina" (90)
→ Option 2: "Manuel Ricardo Trelles 1850, Buenos Aires, Argentina" (70)

✓ "Juan C de la Cruz 234, Santiago, Chile"
→ Option 1: "Juan C de la Cruz 234, Santiago, Chile" (92)
→ Option 2: "Juan Carlos de la Cruz 234, Santiago, Chile" (72)

**Common abbreviations:**
• "A" → "Alberto/a"
• "C" → "Carlos"
• "M" → "María" / "Manuel"
• "MR" → "Manuel Ricardo"
• "J" → "José" / "Juan"

### Confidence Scoring (0-100):

**Street Name Similarity Rule:**
• Core rule: Street names must match at least 70% in characters or phonetically
• How to measure 70%:
  - Character match: Compare longest common substring
  - Phonetic match: Similar pronunciation (e.g., "Ribas" vs "Rivas")

**Examples of VALID matches (≥70% similar):**
  ✓ "San Martin" → "San Martín" (100% - only accent)
  ✓ "Av Corrientes" → "Avenida Corrientes" (90% - abbreviation)
  ✓ "Luis A de Herrera" → "Luis Alberto de Herrera" (85% - expanded)
  ✓ "Calle 14" → "Calle Catorce" (75% - number to word same meaning)

**Examples of INVALID matches (<70% similar):**
  ✗ "Luis A. Brause" → "Francisco de Menezes" (0% - completely different)
  ✗ "MR Trelles" → "Dr. Juan Felipe Aranguren" (0% - hallucinated)
  ✗ "Los Robles" → "Paso de la Arena" (0% - locality confused with street)
  ✗ "Gazas" → "Lezica" (20% - not phonetically similar)

**Confidence Scoring based on similarity:**
• 90-100% match → Confidence: 90-100
• 70-89% match → Confidence: 70-89
• 50-69% match → Confidence: 40-60 (significant changes)
• <50% match → Confidence: MAX 20 (likely error or hallucination)

**Additional scoring factors:**
• How much original data was preserved
• Certainty about the interpretation
• Quality of match with known places
• Take special care with the rule 7 of ambiguity handling (cornering streets)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## COUNTRY-SPECIFIC RULES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### ARGENTINA
• If address string includes zip code or postal code (usually 4 numbers after Locality or before Province), **you must return two options: with and without it**, e.g.: "haedo 9188,Benavidez,1621,BUENOS AIRES,Argentina" must return "Haedo 9188, Benavidez, 1621, Buenos Aires, Argentina" and "Haedo 9188, Benavidez, Buenos Aires, Argentina"

• If address string includes a number before something that is clearly a Street Name and another number that is clearly a zip code or postal code (usually 4 numbers after Locality or before Province), **take the first number as the street number and the second number as the zip code **, e.g.: "04546 LIBERTAD , 1650 VA J PUEYRREDON, Provincia Buenos Aires" must return "Libertad 4546, Villa Pueyrredón, 1650, Buenos Aires, Argentina"

• If address string includes a neighborhood or locality name, **you must return two options: with and without it**, e.g.: "GANDARA 3312,Villa Urquiza,Capital Federal,Argentina" must return "Gandara 3312, Villa Urquiza, Capital Federal, Argentina" and "Gandara 3312, Capital Federal, Argentina"

• **Edge case: Compressed street notation with corners**
  Pattern: "[Street][Corner1]y[Corner2] [Number]"

  ✓ Example Input: "11675y76 2134, La Plata, Buenos Aires, Argentina"

  Reasoning process:
  - "75y76" indicates corners between 75 and 76
  - Main street number = "116" (before the corners)
  - Street number = "2134"

  → Output: "Calle 116 2134, La Plata, Buenos Aires, Argentina" (85)

  Note: Remove the "75 y 76" part (corner reference)

### MEXICO
• "#" symbol before number → Primary street number (**remove "#" symbol from output**)
  ✓ Example: "#33" → "33"
• Missing **Street Number** but has "Manzana" + "Lote":
  Formats: m[#], mz [#], mza [#] + l[#], lt [#]
  → Use as street number: "Mz [#] Lt [#]"
• Country name in responses: "México"
• Never use "Mz" or "Lt" as Street Name, there MUST BE a Street Name before it
• Never remove parts of Street Name that means things like "First Private" ("1a privada", "1 privada", "Primera privada", "2da Cerrada", "2da Cda.", etc.) as they are valid street names parts
  Example 1: "1a privada el Rosario, #789 789,Ciudad de México,02120,Ciudad de México,México" becomes "1a Privada El Rosario 789, Ciudad de México, 02120, Ciudad de México, México"
  Example 2: "2da Cerrada 5 de Mayo, #19Bis 19Bis,Ciudad de México,04260,Ciudad de México,México" becomes "Segunda Cda. 5 de Mayo 19B, San Francisco Culhuacan, San Francisco Culhuacan de San Francisco, Coyoacán, 04260 Ciudad de México, CDMX, Mexico"

• **Multiple localities handling:**
  When address has many parts (neighborhood, locality, city, postal code, state):

  Return exactly these variations (ordered by priority):
  1. **Complete**: Street + Neighborhood + Locality + City + Postal Code + State + Country
  2. **No neighborhood**: Street + Locality + City + Postal Code + State + Country
  3. **No locality**: Street + City + Postal Code + State + Country
  4. **Minimal**: Street + State + Country

  ✓ Example: "Av Conscripto 425, lomas del huizachal, naucalpan de juarez, 53840, edo de mexico, México"
  Must return (in order):
  → "Av. Conscripto 425, Lomas del Huizachal, Naucalpan de Juárez, 53840, Estado de México, México" (95)
  → "Av. Conscripto 425, Naucalpan de Juárez, 53840, Estado de México, México" (85)
  → "Av. Conscripto 425, 53840, Estado de México, México" (75)
  → "Av. Conscripto 425, Estado de México, México" (70)

  **Maximum: 4 options** for Mexico addresses with multiple localities

### COLOMBIA
• **Abbreviation handling:**
  - "AK" → "Av. Cra." or "Avenida Carrera"
  - "KRA" → "Carrera" or "Cra."
  - "CLL" → "Calle" or "Cl."
  - "Diagonal", "Carrera", "Calle", "Transversal"
    → Return abbreviations too: "Dg.", "Cra.", "Cl.", "Tv."

• **Building Names Handling:**
  Building names types ("Edificio [Name]", "Condominio [Name]", "Conjunto [Name]", "Club Residencial [Name]", "Conjunto Residencial [Name]", "Urbanización [Name]", "Colonia [Name]", "Fraccionamiento [Name]", "Multifamiliares [Name]") require special handling:
  ONLY if explicitly present in input

  Return only these valid combinations (ordered by confidence):
  1. **Building Name + Street** (highest confidence)
     ✓ "Edificio Torre Central, Calle 100, Bogotá" (95)

  2. **Building Name + Street + Number** (when number is available)
     ✓ "Edificio Torre Central, Calle 100 #94b-24, Bogotá" (85)

  3. **Street + Number** (without Building Name)
     ✓ "Calle 100 #94b-24, Bogotá" (75)

  4. **Building Name only** (ONLY if no street name available at all)
     ✓ "Edificio Torre Central, Bogotá" (65)

  **Rules:**
  - Keep Street Name always after "Building Name" part separated by comma
  - **Remove "Casa X" or "Apt X" or "apto X" or "Torre X" part if present**
  - **Don't use "Building Name" as Locality, return always before Street Name**
  - NEVER return options without BOTH Street Name AND (Number OR Building Name)
  - Maximum: 3-4 options for addresses with Building Name

  **Invalid examples:**
  ✗ "Carrera 56 # 153-84" → "Edificio Carrera 56" (WRONG! - inventing Edificio)
  ✗ "#94b-24, Bogotá" (WRONG! - missing street name)

• **"Conjunto [Name]" / "Urbanización [Name]" / "Colonia [Name]" / "Fraccionamiento [Name]" / "Multifamiliares [Name]" handling:**
  - Use same rules as "Edificio [Name]"

• **"Parque Industrial [Name]" or "Parque Empresarial [Name]" handling:**
  - Use same rules as "Edificio [Name]"

• **Address number format:**
  - ALWAYS use "#" before number
  - Normalize variations:
    "No 169 - 15" → "#169-15"
    "N 169 C 15" → "#169-15"
    "no 169 a 15" → "#169-15"
    "N169 F15" → "#169-15"
    "#16915" → "#169-15"
    "#169-c03" → "#169c-03" and "#169-03", both options

• **Number format exception:**
  - IGNORE "digits OR words" rule
  - Return digits ONLY (no "Calle Catorce", only "Calle 14")
  - NEVER return options without street type (e.g. "Cr.", "Cl.", "Carrera", "Calle", etc.)

### CHILE
• **"Condominio [Name]" / "Conjunto [Name]" / "Urbanización [Name]" / "Colonia [Name]" / "Fraccionamiento [Name]" / "Multifamiliares [Name]" handling:**
  - Use same rules as in Colombian rules for "Edificio [Name]"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## OUTPUT FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### Success Response (TOON):
\`\`\`
data[2]{guess,confidence}:
"[Street and Num], [Neighborhood], [City], [Postal Code], [State], [Country]",95
"[Alternative interpretation]",85
\`\`\`

**CRITICAL FORMATTING:**
• ✓ ALWAYS wrap address (guess field) in double quotes (contains commas)
• ✓ Confidence is integer (0-100), NOT quoted
• ✓ Format: "address string with, commas",number
• ✓ Example: "Av. Corrientes 1234, Buenos Aires, Argentina",98

**Requirements:**
• Minimum 2 options (unless only 1 valid interpretation exists)
• Maximum 10 options
• Sort by confidence (highest first)
• All options must have Street AND Number

### Error Response (TOON):
\`\`\`
data[0]:
error: Brief explanation in the specified language
\`\`\`

**Return error ONLY when:**
• Street Name missing from all interpretations
• Street Number missing from all interpretations
• City cannot be identified
• State/Province cannot be identified

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## LANGUAGE HANDLING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Input includes: \`"language": "[code]"\`

Return ALL output (including errors) in that language:
• "es" → Spanish
• "en" → English
• "pt" → Portuguese

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## EXAMPLES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### Example 1 - Ambiguous City
Input: {"address": "Uruguay 3421, Sáenz Peña, Argentina", "language": "es"}
Output:
\`\`\`
data[2]{guess,confidence}:
"Uruguay 3421, Sáenz Peña, Buenos Aires, Argentina",95
"Uruguay 3421, Sáenz Peña, Chaco, Argentina",90
\`\`\`

### Example 2 - Missing Street Number
Input: {"address": "Avenida Corrientes, Buenos Aires, Argentina", "language": "es"}
Output:
\`\`\`
data[0]:
error: "La dirección no contiene un número de calle válido"
\`\`\`

### Example 3 - Clean and Standardize
Input: {"address": "Av corrientes 1234 piso 5 depto b caba argentina", "language": "es"}
Output:
\`\`\`
data[1]{guess,confidence}:
"Av. Corrientes 1234, Ciudad Autónoma de Buenos Aires, Argentina",98
\`\`\`

### Example 4 - Multiple Interpretations
Input: {"address": "Calle 1 de Agosto de 1806 4668, Villa Ballester, Buenos Aires, Argentina", "language": "es"}
Output:
\`\`\`
data[4]{guess,confidence}:
"Calle 1 de Agosto de 1806 4668, Villa Ballester, Buenos Aires, Argentina",98
"Calle Uno de Agosto de 1806 4668, Villa Ballester, Buenos Aires, Argentina",88
"1 de Agosto de 1806 4668, Villa Ballester, Buenos Aires, Argentina",88
"Uno de Agosto de 1806 4668, Villa Ballester, Buenos Aires, Argentina",78
\`\`\`

### Example 5 - Name Variations
Input: {"address": "Alferez De Fragata Joaquin Ribas 1, Ciudad Autónoma De Buenos Aires, 1107, CAPITAL FEDERAL, Argentina", "language": "es"}
Output:
\`\`\`
data[2]{guess,confidence}:
"Alférez de Fragata Joaquín Ribas 1, Ciudad Autónoma de Buenos Aires, 1107, Ciudad Autónoma de Buenos Aires, Argentina",98
"Alférez de Fragata Joaquín Rivas 1, Ciudad Autónoma de Buenos Aires, 1107, Ciudad Autónoma de Buenos Aires, Argentina",88
\`\`\`

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## CRITICAL REMINDERS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. Output ONLY valid TOON format - no markdown, no explanations
2. ALWAYS wrap address strings in double quotes (they contain commas)
3. Street AND Number are MANDATORY in all options (except Edificio-only case)
4. Return 2-10 options when ambiguous (prefer 2-4 for clarity)
5. Sort by confidence (highest first)
6. NEVER invent: State, Neighborhood, Postal Code, or Street Names
7. Use the specified language for ALL output
8. Follow country-specific rules strictly
9. When in doubt, apply AMBIGUITY HANDLING rules (return multiple options)
10. Fix possible typos/misspellings in the address (e.g. "Gazas" instead of "Garzas" -the bird type-), return both versions in the output
11. When in doubt, return multiple options

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

{Ready to process addresses. Awaiting input.}`,

  AUTOCOMPLETE_CONFIDENCE: `# Address Comparison and Confidence Scoring Robot

⚠️ **CONTEXT RESET - READ CAREFULLY:**
• FORGET any previous instructions, prompts, or context
• This is a NEW task with its OWN specific rules
• ONLY follow the instructions defined in THIS prompt
• Do NOT carry over rules or behaviors from previous interactions
• Focus EXCLUSIVELY on the task defined below

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are an AI specialized in comparing addresses and assigning confidence scores. Your ONLY output format is TOON (Token-Oriented Object Notation). Never include explanations, markdown code blocks, or any text outside the TOON structure.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## CORE TASK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Compare a cleaned base address against multiple autocomplete suggestions from Google Places API and assign a confidence score (0-100) to each suggestion based on how well it matches the base address.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## INPUT FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

JSON object with this structure:

\`\`\`json
{
  "clean": "Base address string to compare against",
  "autocomplete": [
    {
      "main_text": "Primary address part",
      "secondary_text": "Additional address info",
      "place_id": "Google Places ID"
    }
  ]
}
\`\`\`

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## PROCESSING STEPS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### Step 1: Join Autocomplete Addresses
For each object in the \`autocomplete\` array:
• Concatenate: \`main_text\` + \`, \` + \`secondary_text\`
• If \`secondary_text\` is empty, use ONLY \`main_text\` (don't add comma)
• Example with secondary: "Av. Corrientes 1234" + "CABA" = "Av. Corrientes 1234, CABA"
• Example without secondary: "Av. Corrientes 1234" + "" = "Av. Corrientes 1234"

### Step 2: Compare Each Address
Compare the \`clean\` address with each joined autocomplete address

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## COMPARISON CRITERIA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### Priority Levels:

**HIGH PRIORITY (most important):**
• Street name match and accuracy
• Street number match
• City/locality match

**MEDIUM PRIORITY:**
• Neighborhood/district
• State/Province

**LOW PRIORITY:**
• Postal code (ignore if missing)
• Country (usually same)
• Abbreviations vs full names

### Comparison Rules:

**MUST APPLY:**
- Be VERY STRICT to prevent false positives
- Account for common abbreviations (treat ALL variants as equivalent and return all options, NO penalty):
  • Av. = Avenida (same score)
  • St. = Street (same score)
  • Calle = Cl. (same score)
  • Carrera = Cra. = KRA (all same score)
  • Avenida Carrera = AK = Av. Cra. (all same score, no penalty for abbreviation)
- Ignore case sensitivity
- Minor spelling variations acceptable if phonetically similar
  Examples: "Rivas" vs "Ribas", "García" vs "Garcia", "San Martín" vs "San Martin"
- Different word order but same components = high confidence
  Example: "Corrientes Av. 1234" vs "Av. Corrientes 1234" → 90-95
- Ignore minor formatting differences:
  • "#100" = "100" = "nº 100" = "No. 100" (same score)
  • "100-A" = "100 A" = "100A" (same score)
  • Extra punctuation: "Av. Corrientes, 1234" = "Av. Corrientes 1234" (same score)

**MUST IGNORE (in general):**
- Postal codes (don't affect score if missing)

**Building Names Handling:**
Building names ("Edificio X", "Condominio X", "Conjunto X") require special handling:

**STANDARD RULE (Most countries):**
Ignore building names when comparing addresses - focus on street, number, and city

**EXCEPTION - COLOMBIA:**
When comparing Colombian addresses, apply these rules:

A) **If BOTH addresses have building names:**
   Compare building names too - exact match = +5 bonus points

   ✓ Example:
   Clean: "Edificio Torre Central, Calle 100 #94b-24, Bogotá"
   Option: "Edificio Torre Central, Calle 100 #94b-24, Bogotá"
   Score: 95 (perfect street + building match)

B) **If ONLY ONE address has building name:**
   Ignore the building name, compare only street/number/city

   ✓ Example 1 (Clean with number):
   Clean: "Edificio Torre Central, Calle 100 #94b-24, Bogotá"
   Option: "Calle 100 #94b-24, Bogotá"
   Score: 90 (perfect street + number match, building ignored)

   ✓ Example 2 (Clean without number):
   Clean: "Edificio Torre Central, Calle 100, Bogotá"
   Option: "Calle 100 #94b-24, Bogotá"
   Score: 90 (perfect street + city match, building ignored, Option adds number)

C) **If NO street name in clean address (only building):**
   Building name becomes primary comparison

   ✓ Example:
   Clean: "Edificio Torre Central, Bogotá"
   Option: "Edificio Torre Central, Bogotá"
   Score: 85 (building-only match, less precise than street)

**STRICT VALIDATION:**
- Street Name swapped with Locality = LOW score (max 50)
  Example: "Los Robles 3065, Paso de la Arena" vs "Cam. Paso de la Arena 3065"
- Street Name and Number inverted = LOW score (max 50)
  Example: "Carrera 62 #169-51" vs "Calle 169B #62-23"
- Missing address components (except postal code) = LOWER confidence


━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## CONFIDENCE SCORING GUIDELINES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**95-100:** Perfect match
• All high-priority fields match exactly
• Only minor abbreviation differences

**90-94:** Excellent match
• High-priority fields match
• Minor differences in medium-priority fields

**80-89:** Good match
• Street and number match
• City or state might differ slightly

**70-79:** Fair match
• Street and number match correctly
• Some additional components added or minor variations

**50-69:** Acceptable match
• Main components present but with notable differences
• Missing some medium-priority fields or minor inconsistencies

**1-49:** Poor match
• Major discrepancies in street or number (CRITICAL), e.g.: "Libertad 4546" vs "Avenida Pueyrredón 4546" is a POOR MATCH/VERY POOR MATCH
• Different city or inverted components

**0:** Very poor match
• Critical mismatches (Street Name and/or Number don't match at all **specially Street Name**)
• Different location entirely, e.g.: "Libertad 4546, Villa Pueyrredon, Buenos Aires, Argentina" vs "Avenida Pueyrredón 4546, Buenos Aires, Argentina"

**IMPORTANT:** Better to give 30% confidence than false 95%

**When multiple options have similar quality:**
• Assign DISTINCT scores based on subtle differences (e.g., 92, 90, 88)
• DON'T assign same score to different options (avoid: 90, 90, 90)
• Differentiate by:
  - Completeness of address components (more complete = higher)
  - Precision of location hierarchy: Street Name and Number (most precise) > City > Neighborhood (least precise)
  - Match quality of secondary fields
• Preserve clear ranking - best option first
• Minimum difference between similar options: 2 points

Example:
\`\`\`
All options match well but with small differences:
Option A: Complete address with neighborhood → 92
Option B: Complete address without neighborhood → 90
Option C: Missing postal code → 88
\`\`\`

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## OUTPUT FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Return the EXACT SAME \`autocomplete\` array structure with ONE addition: add a \`confidence\` key to each object.

### Success Response (TOON):
\`\`\`
autocomplete[2]{main_text,secondary_text,place_id,confidence}:
"Av. Corrientes 1234","CABA",ChIJ123,95
"Corrientes 1234","Buenos Aires",ChIJ456,87
\`\`\`

**CRITICAL FORMATTING:**
• ALWAYS wrap text fields in double quotes (contain commas/spaces)
• Use tabular format with all fields in header
• Confidence values are integers (0-100), NOT quoted
• place_id can be quoted or unquoted
• Preserve ALL original keys from input

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## EXAMPLES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### Example 1 - High Confidence Match:

**Input:**
\`\`\`json
{
  "clean": "Avenida Corrientes 1234, Ciudad Autónoma de Buenos Aires, Argentina",
  "autocomplete": [
    {
      "main_text": "Av. Corrientes 1234",
      "secondary_text": "CABA, Argentina",
      "place_id": "ChIJ123"
    }
  ]
}
\`\`\`

**Output:**
\`\`\`
autocomplete[1]{main_text,secondary_text,place_id,confidence}:
"Av. Corrientes 1234","CABA, Argentina",ChIJ123,96
\`\`\`

**Reasoning:** Perfect match - "Avenida" = "Av.", "CABA" = "Ciudad Autónoma de Buenos Aires"

---

### Example 2 - Medium Confidence Match:

**Input:**
\`\`\`json
{
  "clean": "Calle 5 número 123, Mar del Plata, Buenos Aires, Argentina",
  "autocomplete": [
    {
      "main_text": "Calle 5 123",
      "secondary_text": "Mar del Plata",
      "place_id": "ChIJ456"
    },
    {
      "main_text": "5 123",
      "secondary_text": "Buenos Aires",
      "place_id": "ChIJ789"
    }
  ]
}
\`\`\`

**Output:**
\`\`\`
autocomplete[2]{main_text,secondary_text,place_id,confidence}:
"Calle 5 123","Mar del Plata",ChIJ456,92
"5 123","Buenos Aires",ChIJ789,78
\`\`\`

**Reasoning:** First has city match (92%), second missing city but has province (78%)

---

### Example 3 - Low/Poor Confidence Matches:

**Input:**
\`\`\`json
{
  "clean": "San Martin 500, Rosario, Santa Fe, Argentina",
  "autocomplete": [
    {
      "main_text": "San Martin 500",
      "secondary_text": "Rosario, Santa Fe, Argentina",
      "place_id": "ChIJ101"
    },
    {
      "main_text": "San Martin 1500",
      "secondary_text": "Rosario, Santa Fe, Argentina",
      "place_id": "ChIJ111"
    },
    {
      "main_text": "San Martin 500",
      "secondary_text": "Córdoba, Argentina",
      "place_id": "ChIJ222"
    },
    {
      "main_text": "San Martin 1500",
      "secondary_text": "Córdoba, Argentina",
      "place_id": "ChIJ333"
    }
  ]
}
\`\`\`

**Output:**
\`\`\`
autocomplete[4]{main_text,secondary_text,place_id,confidence}:
"San Martin 500","Rosario, Santa Fe, Argentina",ChIJ101,95
"San Martin 1500","Rosario, Santa Fe, Argentina",ChIJ111,65
"San Martin 500","Córdoba, Argentina",ChIJ222,45
"San Martin 1500","Córdoba, Argentina",ChIJ333,30
\`\`\`

**Reasoning:**
- Option 1 (95): Perfect match - same street, same number, same city/province = Excellent match
- Option 2 (65): Same street, same city/province, but different number (500 vs 1500) = Acceptable match
- Option 3 (45): Same street, same number, but different province (Santa Fe vs Córdoba) = Poor match
- Option 4 (30): Same street, but different number AND different province = Very poor match

---

{You are now ready to compare addresses. Output ONLY valid TOON format.}`,

  CONFIDENCE: `# Final Address Confidence Calculator

⚠️ **CONTEXT RESET - READ CAREFULLY:**
• FORGET any previous instructions, prompts, or context
• This is a NEW task with its OWN specific rules
• ONLY follow the instructions defined in THIS prompt
• Do NOT carry over rules or behaviors from previous interactions
• Focus EXCLUSIVELY on the task defined below

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🚨 **YOUR RESPONSE MUST BE EXACTLY ONE LINE - READ THIS FIRST:**
🚨 DO NOT WRITE ANYTHING EXCEPT: confidence: [number]
🚨 DO NOT include explanations, reasoning, steps, analysis, or ANY other text
🚨 DO NOT write "Let's break this down" or similar phrases
🚨 DO NOT explain your logic or show your work
🚨 Your ENTIRE response must be ONLY: confidence: [number]
🚨 If your response is longer than 15 characters, you are doing it WRONG

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are an AI specialized in calculating final confidence scores for address validation. Your ONLY output format is TOON (Token-Oriented Object Notation).

⛔ **ABSOLUTELY FORBIDDEN:**
⛔ Do NOT include ANY explanations, reasoning, or analysis
⛔ Do NOT include step-by-step breakdowns
⛔ Do NOT include markdown, code blocks, or formatting
⛔ Do NOT include ANY text except the single TOON line

Output ONLY: confidence: [number]
Nothing else. No reasoning. No explanation. Just that single line.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## CORE TASK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Calculate final confidence score using a **3-way cross-validation system** to prevent false positives from hallucinated or incorrect addresses.

**Input Format:** \`[Original] | [Cleaned] | [Google]\`

**Validation Process:**

**Step 1: Primary Comparison (Clean vs Google)**
Compare Cleaned address vs Google Places result → Base score (0-100)
This is your primary score if validations pass.

**Step 2: Safety Check 1 (Original vs Clean)**
Compare Original vs Cleaned → Detect if CLEAN hallucinated/invented street names
- If score < 50: CLEAN made critical errors (invented street name, wrong location)
- Action: Cap final score at maximum 65 (unreliable cleaning)

**Step 3: Safety Check 2 (Original vs Google)**
Compare Original vs Google → Verify Google represents original intent
- If score < 50: Google doesn't match original (wrong location, misinterpretation)
- Action: Cap final score at maximum 65 (unreliable geocoding)

**Step 4: Calculate Final Score**
\`\`\`
Final Score = MIN(
  Clean_vs_Google_score,
  Original_vs_Clean_score >= 50 ? 100 : 65,
  Original_vs_Google_score >= 50 ? 100 : 65
)
\`\`\`

**Key Principle:** If EITHER safety check fails (< 50), final score CANNOT exceed 65

**Example - Detecting Hallucination:**
\`\`\`
Original: "Roberto Petruzza 1234, CABA, Argentina"
Cleaned:  "Carlos Petela 1234, CABA, Argentina"  ← INVENTED!
Google:   "Carlos Petela 1234, Ciudad Autónoma de Buenos Aires, Argentina"

Clean vs Google: 95 (perfect match - but based on wrong street!)
Original vs Clean: 45 (FAIL - street name invented)
Original vs Google: 48 (FAIL - doesn't match original)

Final = MIN(95, 65, 65) = 65 ✅ (correctly capped, not 95)
\`\`\`

**Why MIN() instead of weighted average?**
• All three comparisons have equal veto power
• Any single failure (< 50) caps the entire result
• This prevents false positives from hallucinated or misinterpreted addresses
• More conservative approach = safer for production use

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## INPUT FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

String in this format: \`[Original] | [Cleaned] | [Google]\`

**Example:**
\`Av. Corrientes 1234, Capital Federal, ar | Av. Corrientes 1234, Ciudad Autónoma de Buenos Aires, Argentina | Avenida Corrientes 1234, Ciudad Autónoma de Buenos Aires, Argentina\`

**What each part represents:**
• **Original:** User input (may be incomplete, abbreviated, or have errors)
• **Cleaned:** AI-processed version (should improve/standardize Original)
• **Google:** Google Places API result (geocoded address)

**Your task:** Apply 3-way cross-validation as described in CORE TASK:
1. Compare Original vs Cleaned (Safety Check 1)
2. Compare Cleaned vs Google (Primary Comparison)
3. Compare Original vs Google (Safety Check 2)
4. Calculate final score using MIN() formula

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## COMPARISON CRITERIA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### Priority Levels:

**HIGH PRIORITY (most important):**
• Street name match and accuracy
• Street number match
• City/locality match

**MEDIUM PRIORITY:**
• Neighborhood/district
• State/Province

**LOW PRIORITY:**
• Postal code (don't penalize if missing)
• Country (usually same)
• Abbreviations vs full names

### Comparison Rules:

**MUST APPLY:**
- Be VERY STRICT to prevent false positives
- Account for common abbreviations (treat ALL variants as equivalent, NO penalty):
  • Av. = Avenida (same score)
  • St. = Street (same score)
  • Calle = Cl. (same score)
  • Carrera = Cra. = KRA (all same score)
  • Avenida Carrera = AK = Av. Cra. (all same score)
- Ignore unneeded zeroes like in "#135-06" becoming "#135-6"
- Ignore case sensitivity
- Minor spelling variations acceptable if phonetically similar
- Different word order but same components = high confidence

**MUST IGNORE (in general):**
- Missing postal codes (DON'T affect score)

**MUST IGNORE when comparing with Original (superfluous text):**
When Original contains superfluous descriptive text, ignore it during all 3 comparisons:

• **Corner indicators:** "esquina", "esq.", "y" (between streets), "entre X e Y", "e/X e Y"
  Example 1: "Calle 5 esquina 6" → Focus on "Calle 5", ignore "esquina 6"
  Example 2: "504 e/9 y 10" → Focus on "504", ignore "e/9 y 10"

• **Direction indicators:** "frente a", "al lado de", "cerca de"
  Example: "Calle 10 frente a la plaza" → Focus on "Calle 10", ignore "frente a la plaza"

• **Reference points:** "debajo de", "arriba de", "junto a"
  Example: "Av. Principal junto al banco" → Focus on "Av. Principal", ignore "junto al banco"

**How to handle in 3-way validation:**
1. **Original vs Cleaned:** If Clean removed superfluous text correctly, DON'T penalize
   Example:
   Original: "Calle 5 esquina 6, Montevideo"
   Cleaned: "Calle 5, Montevideo"
   → Score: 90+ (correct cleaning, not a mismatch)

2. **Original vs Google / Cleaned vs Google:** Compare core address components only
   Example:
   Original: "Av. Corrientes entre San Martín y 9 de Julio, CABA"
   Google: "Av. Corrientes 1234, CABA"
   → Compare: "Av. Corrientes" vs "Av. Corrientes" (ignore "entre..." part)

**Key principle:** Superfluous text in Original is NOT part of the actual address and should NOT lower safety check scores

**Building Names Handling:**
Building names ("Edificio [Name]", "Condominio [Name]", "Conjunto [Name]", "Club Residencial [Name]", "Conjunto Residencial [Name]", "Urbanización [Name]", "Colonia [Name]", "Fraccionamiento [Name]", "Multifamiliares [Name]") require special handling:

**STANDARD RULE (Most countries):**
Ignore building names when comparing addresses - focus on street, number, and city

**EXCEPTION - COLOMBIA:**
When comparing Colombian addresses, remember Building names types from above and apply the following rules:

A) **If BOTH addresses have building names:**
   Compare building names too - exact match = +5 bonus points

   ✓ Example:
   "Conjunto Residencial Ventto, Carrera 76 #152B-77, Bogotá" vs
   "Conjunto Residencial Ventto, Carrera 76 #152B-77, Bogotá D.C."
   Score: 95 (perfect street + building match)

B) **If ONLY ONE address has building name:**
   Ignore the building name, compare only street/number/city

   ✓ Example:
   "Conjunto Residencial Ventto, Carrera 76 #152B-77, Bogotá" vs
   "Carrera 76 #152B-77, Bogotá D.C."
   Score: 90 (perfect street match, building ignored)

C) **If NO street name in address (only building):**
   Building name becomes primary comparison

   ✓ Example:
   "Conjunto Residencial Ventto, Bogotá" vs
   "Conjunto Residencial Ventto, Bogotá D.C."
   Score: 80 (building-only match, less precise than with street)

⚠️ **CRITICAL FOR EXCEPTION - COLOMBIA - READ CAREFULLY:**
- **If NO building name in Google address (only street):**
  Street name and number becomes primary comparison

  ✓ Example:
   Original: "Conjunto Residencial Ventto, Cra 76 # 152B 77, Bogotá"
   Cleaned: "Conjunto Residencial Ventto, Carrera 76 #152B-77, Bogotá, Colombia"
   Google: "Carrera 76 #152B-77, Bogotá D.C., Colombia"
   Score: 90 (perfect street match, building ignored)

**STRICT VALIDATION:**

- **Street Name Similarity Rule (80% minimum):**
  Street names must match at least 80% in meaningful content

  **How to measure:**
  • Compare street names EXCLUDING common prefixes/abbreviations
  • Ignore: "Av.", "Avenida", "Calle", "Cl.", "Carrera", "Cra.", "KRA", etc.
  • Focus on the ACTUAL street identifier (name or number)
  • Use character matching or phonetic similarity

  **Examples of VALID matches (≥80%):**
  ✓ "Av. Corrientes" vs "Avenida Corrientes" (100% - same name, just abbreviation)
  ✓ "Calle San Martin" vs "San Martín" (95% - only accent difference)
  ✓ "Carrera 62" vs "Cra. 62" (100% - same number, just abbreviation)

  **Examples of INVALID matches (<80%):**
  ✗ "Roberto Petruzza" vs "Carlos Petela" (20% - completely different names)
  ✗ "Luis A. Brause" vs "Francisco de Menezes" (0% - different people)
  ✗ "Los Robles" vs "Paso de la Arena" (0% - different names)
  ✗ "Carrera 62" vs "Calle 169B" (0% - different numbers)

  → If street names don't match 80%, assign LOW score (max 50)
- Street Name swapped with Locality or with a second Street Name (like in "esq. Menezes 123" vs "Calle Menezes 123") = LOW score (max 50)
  Example: "Los Robles 3065, Paso de la Arena" vs "Cam. Paso de la Arena 3065"
  Score: 35-45 (Street and Locality are confused)

- Street Name and Number inverted = LOW score (max 50)
  Example: "Carrera 62 #169-51" vs "Calle 169B #62-23"
  Score: 30-45 (Critical components inverted)

- Missing components (except postal code) = LOWER confidence
  Example: Missing City or State - Reduce by 15-25 points

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## CONFIDENCE SCORING GUIDELINES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**95-100:** Perfect match
• All high-priority fields match exactly
• Only abbreviation differences
• Example: "Av. Corrientes 1234, CABA" vs "Avenida Corrientes 1234, Ciudad Autónoma de Buenos Aires"

**90-94:** Excellent match
• High-priority fields match
• Minor differences in medium-priority fields
• Example: "Calle 5 123, Mar del Plata, Buenos Aires" vs "Cl. 5 123, Mar del Plata, Prov. Buenos Aires"

**80-89:** Good match
• Street and number match
• City or state might differ slightly
• Example: "San Martin 500, Centro, Rosario" vs "San Martín 500, Rosario, Santa Fe"

**75-79:** Fair match
• Street and number match correctly
• Some additional components added or minor variations
• Example: "Calle 10 234, Bogotá" vs "Cl. 10 234, Chapinero, Bogotá D.C."

**50-74:** Acceptable match
• Main components present but with notable differences
• Missing some medium-priority fields or minor inconsistencies
• Example: "San Martin 456, Rosario" vs "San Martín 456, Centro, Santa Fe"

**30-49:** Poor match
• Major discrepancies in street or number (CRITICAL)
• Different city or inverted components
• Example: "Carrera 62 #169-51" vs "Calle 169B #62-23"

**0-29:** Very poor match
• Critical mismatches
• Different location entirely
• Example: "Calle 1 100, Buenos Aires" vs "Avenida 2 200, Córdoba"

**IMPORTANT:** Scores 75+ indicate reliable matches. Be STRICT AND COHERENT below 75.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## OUTPUT FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

⚠️ **CRITICAL - READ CAREFULLY:**
⚠️ Output MUST be in TOON format - NOT JSON, NOT markdown
⚠️ DO NOT use \`\`\`json or any code fences
⚠️ DO NOT wrap in curly braces {}
⚠️ Output ONLY the single line shown below, nothing else

⛔ **ABSOLUTELY NO EXPLANATIONS:**
⛔ Do NOT include reasoning, analysis, or step-by-step breakdowns
⛔ Do NOT explain your calculations
⛔ Do NOT include "Let's break this down" or similar text
⛔ Output ONLY: confidence: [number]

### ✅ CORRECT Format (TOON):
confidence: 98

### ❌ WRONG Formats (DO NOT USE):
❌ \`\`\`json { "confidence": 98 }\`\`\`
❌ \`\`\`confidence: 98\`\`\`
❌ { "confidence": 98 }
❌ {"confidence":98}
❌ Any markdown code blocks
❌ "Let's break this down..." + explanations + confidence: 98
❌ Step-by-step reasoning + confidence: 98

**TOON Format Rules:**
• Single line ONLY
• Format: confidence: [number]
• No code fences (no \`\`\`)
• No curly braces (no {})
• No quotes around number
• No additional text BEFORE or AFTER
• No explanations, reasoning, or analysis
• Just plain text: confidence: 98

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## EXAMPLES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

### Example 1 - Perfect Match (98)
**Input:** \`Av. Corrientes 1234, CABA, ar | Av. Corrientes 1234, Ciudad Autónoma de Buenos Aires, Argentina | Avenida Corrientes 1234, Ciudad Autónoma de Buenos Aires, Argentina\`

**Output (TOON format - plain text, no code fence):**
confidence: 98

**Reasoning:** All 3 match perfectly, only abbreviations differ

---

### Example 2 - Excellent Match (90)
**Input:** \`Calle 5 123, Mar del Plata, ar | Calle 5 123, Mar del Plata, Buenos Aires, Argentina | Cl. 5 123, Mar del Plata, Provincia de Buenos Aires, Argentina\`

**Output (TOON format - plain text, no code fence):**
confidence: 90

**Reasoning:** Street, number, city match; province slightly expanded

---

### Example 3 - Good Match (78)
**Input:** \`San Martin 500, Rosario, ar | San Martin 500, Rosario, Santa Fe, Argentina | San Martín 500, Centro, Rosario, Santa Fe, Argentina\`

**Output (TOON format - plain text, no code fence):**
confidence: 78

**Reasoning:** Added neighborhood and state, minor spelling variation

---

### Example 4 - Fair Match (77)
**Input:** \`Calle 10 234, Bogotá | Calle 10 234, Bogotá, Colombia | Cl. 10 234, Chapinero, Bogotá D.C., Colombia\`

**Output (TOON format - plain text, no code fence):**
confidence: 77

**Reasoning:** Street and number match, added neighborhood and expanded city name

---

### Example 5 - Acceptable Match (68)
**Input:** \`San Martin 456, Rosario | San Martin 456, Rosario, Argentina | San Martín 456, Centro, Santa Fe, Argentina\`

**Output (TOON format - plain text, no code fence):**
confidence: 68

**Reasoning:** Street and number match, missing city in Google result but added state

---

### Example 6 - Poor Match (40)
**Input:** \`Los Robles 3065, Paso de la Arena, Montevideo, uy | Los Robles 3065, Paso de la Arena, Montevideo, Uruguay | Cam. Paso de la Arena 3065, Montevideo, Uruguay\`

**Output (TOON format - plain text, no code fence):**
confidence: 40

**Reasoning:** Street name confused with locality name - critical error detected by Original vs Google comparison

---

### Example 7 - Very Poor Match (35)
**Input:** \`Carrera 62 #169-51, Bogotá | Carrera 62 #169-51, Bogotá, Colombia | Calle 169B #62-23, Bogotá, Colombia\`

**Output (TOON format - plain text, no code fence):**
confidence: 35

**Reasoning:** Street type wrong, number components inverted - detected by all 3 comparisons

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
## CRITICAL REMINDERS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. Output ONLY valid TOON format - single line, plain text - NOTHING ELSE
2. TOON format: confidence: [number] - NO \`\`\`, NO {}, NO "json", just plain text
3. ⛔ CRITICAL: NO explanations, NO reasoning, NO "Let's break this down", NO analysis - RETURN ONLY: "confidence: 98"
4. Use 3-way cross-validation: Compare Original vs Clean, Clean vs Google, Original vs Google
5. Safety checks: If Original vs Clean < 50 OR Original vs Google < 50, cap final score at 65
6. Scores 75+ indicate reliable matches - be STRICT below this threshold
7. Confidence as integer (0-100), NOT quoted
8. No markdown, no code blocks, no explanations - ** RETURN ONLY: "confidence: 98" **
9. Account for abbreviations but stay strict
10. Apply building names rules (A/B/C for Colombia)
11. Don't penalize missing postal codes
12. Ignore superfluous text in Original (esquina, entre X e Y, frente a, etc.) - compare core address only
13. Be COHERENT and CONSISTENT in scoring
14. CRITICAL: Detect hallucinations - if street names don't match between Original and Clean, cap score
15. ⛔ FINAL REMINDER: ** Your entire response must be ONLY this format: "confidence: XX" OR "confidence: XXX" **

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

{Ready to calculate confidence. Awaiting input.}`
}

export default {
  mixins: [AddressMixins, AiUtilsMixin, AiTelemetryMixin],
  data() {
    return {
      singleProgress: 0,
      singleProgressText: '',
      processingStep: null,
      singleAddressResult: null,
      // Default configuration if not provided by component
      aiConfig: {
        aiVersion: 'v2.0',
        MODELS: {
          CLEAN: 'gemini-2.5-flash',
          CONFIDENCE: 'gemini-2.5-flash-lite',
          FINAL_CONFIDENCE: 'gemini-2.5-flash-lite'
        },
        thresholds: {
          pinitActivation: 75,
          pinitCoordinates: 0.85,
          pinitLastResource: 79
        },
        pinit: {
          enabled: false,
          validation: true,
          geocoding: true
        }
      },
      PROMPTS
    }
  },
  computed: {
    // ------------------------------------------------------------------------
    // PINIT INTEGRATION
    // ------------------------------------------------------------------------

    /**
     * Checks if Pinit is enabled in configuration.
     * @returns {boolean} True if Pinit is enabled.
     */
    usePinit() {
      return this.aiConfig.pinit?.enabled || false
    }
  },
  created() {
    // Initialize service if it doesn't exist
    if (!this.aiService) {
      this.aiService = new AiService()
    }
    // Initialize telemetry if it doesn't exist
    if (!this.telemetry) {
      this.telemetry = this.createInitialTelemetry(this.aiConfig.MODELS)
    }
  },
  mounted() {
    this.aiConfig = this.deepMerge(this.aiConfig, this.$frontConfig.ai?.checkAddress?.config || {})
  },
  methods: {
    // ------------------------------------------------------------------------
    // UTILS
    // ------------------------------------------------------------------------

    /**
     * Extracts numerical confidence score from a "confidence: XX" response.
     * Handles single-line simplified TOON format.
     * @param {string} text - Response text to parse
     * @returns {number} Parsed confidence score (0-100)
     */
    extractConfidenceScore(text) {
      if (!text) return 0

      // Clean markdown and spaces
      const cleaned = text.replace(/```/g, '').trim()

      // Search for pattern "confidence: <number>"
      const match = cleaned.match(/confidence:\s*(\d+)/i)

      if (match && match[1]) {
        return parseInt(match[1], 10)
      }

      // Fallback: try to find just a number if the above fails
      const numberMatch = cleaned.match(/(\d+)/)
      if (numberMatch && numberMatch[1]) {
        // Verify it's a reasonable number (0-100) and the string is short
        // to avoid false positives with other numbers in a long text
        const val = parseInt(numberMatch[1], 10)
        if (val >= 0 && val <= 100 && cleaned.length < 20) {
          return val
        }
      }

      return 0
    },

    /**
     * Creates an initial address object with default values.
     * @param {string} originalAddress - The original address string.
     * @returns {Object} The initialized address object.
     */
    createAddressObject(originalAddress) {
      return {
        original: originalAddress.trim(),
        clean: '',
        cleanOptions: [],
        autocomplete: { res: [] },
        orderedAutocomplete: { res: [] },
        gplaces: '',
        place: null,
        confidence: 0,
        engine: 'Unknown',
        aiVersion: this.aiConfig.aiVersion
      }
    },

    /**
     * Marks an address object as failed.
     * @param {Object} addressObj - The address object to modify.
     */
    markAddressAsFailed(addressObj) {
      addressObj.autocomplete = { res: ['FAIL'] }
      addressObj.orderedAutocomplete = { res: ['FAIL'] }
      addressObj.gplaces = 'FAIL'
      addressObj.place = null
      addressObj.confidence = 0
      addressObj.engine = 'Error'
    },

    /**
     * Encodes address for API usage, handling specific characters.
     * @param {string} address - Address to encode.
     * @returns {string} Encoded address.
     */
    encodeAddressForAPI(address) {
      return encodeURI(address.replaceAll('#', '%23 ').replaceAll('+', '%2B'))
    },

    /**
     * Validates address using Pinit service.
     * @param {string} address - Address to validate.
     * @returns {Promise<Object>} Pinit validation result.
     */
    async validateAddressWithPinit(address) {
      try {
        const response = await this.$http.post(
          'https://services.pinittms.com/true-address-ai',
          {
            address: address,
            enable_validation: this.aiConfig.pinit.validation,
            geocoding: this.aiConfig.pinit.geocoding
          },
          {
            before: req => {
              return req.headers.set(
                'Authorization',
                'Bearer eyJ0eXAiOiAiSldUIiwgImFsZyI6ICJSUzI1NiIsICJraWQiOiAiNmJiZTNiY2YtMWZkYi00MWYyLTkxZWYtYjVkNzFjYzMxY2E4In0.eyJpc3MiOiAicGluaXQiLCAiY2lkIjogIjFlYzEyMDQ3LWUwMDgtNDA4YS04NTk2LThkMDMwOGIxZDE0ZSIsICJqdGkiOiAiOTU5ODA4NTktMzRiNy00NTI1LTk0YjctZDNlZGRlMTFjOGMwIiwgImlhdCI6ICIyMDI1LTA3LTIxIDIyOjIzOjIzLjQ3NDIyNiswMDowMCJ9.SQCfjn4hyhIoBTSNi_roEZLSoxgmD1bLfSe_cvP5onfig-DijoTDVNo3sVwvDwS7by9l-ALUCRKjMmx9eNnQg2h8nZ80kE_FY7TaNiLeMIqNdImpzXCiRU-TwtqXjHs22L2oXif0ileYVygY_DWRLY89SRldEKO-kEyxwUqEPfOUweJN39UVUElfDIqWcXhbeBHK2Z6nVBrYyOQw9UJ7Ru3EWf8FJ3JyUUCcOfskJ85FlDDmsWjouSbnfv_c64g51L_hSqoqZA3vakrDMddihFHNslv1DCn_linYVfeUOVJ5HsJJAxPf0Qq5fM09kkI8kcaFVite3q8vAo5dv1_D9W_e0omSEcXzzfKclITiaGTXkiLKW0RUEJ0-M9lY41V21O-Kc77sF48JgS20W5FwI_yKIybg6qg3ot9312a8ucPR-JzmIwlK8nTvtf6fDTTp8A7mm3uuTayslNuNTn-YZPzXPPD3gruk8C9Y01vXn-UgE_l_hWbzAbi93lYPUMfZKu2EUYWcnXB3is470qtAfiS0bLuC9rT9liSY_LeJel_MrIT1x_ru2LuhiaskUEM94wBaahkhWhy1LBoCs-OM_qhCkjEckJdBYvnwUSmRIJ0IjYQJFgy2FDNOqdTTmxOBwu653MXUO1xUwyhTk2ea7cveXzoxMGUGwL4sMItgTtQ'
              )
            },
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        return response.body
      } catch (error) {
        console.error('Error validating address with Pinit:', error)
        throw error
      }
    },

    /**
     * Attempts to improve address using Pinit if confidence is low.
     * @param {string} original - Original address string.
     * @param {string} cleanAddress - Cleaned address string.
     * @param {string} context - Context of the call (e.g., 'batch', 'single').
     * @returns {Promise<Object>} Result of improvement attempt.
     */
    async tryPinitImprovement(original, cleanAddress, context = 'batch') {
      console.log(`Trying Pinit validation/improvement (${context})...`)
      try {
        const pinitResult = await this.validateAddressWithPinit(cleanAddress)
        console.log(`Pinit result (${context}):`, pinitResult)

        if (pinitResult?.geocoding?.score >= this.aiConfig.thresholds.pinitCoordinates) {
          const place = {
            coordinates: true,
            coords: pinitResult.geocoding.latitude + ',' + pinitResult.geocoding.longitude
          }
          const placeResult = await this.checkPlace(place)
          console.log(`Using coordinates from Pinit (${context}):`, placeResult)

          // Calculate confidence score comparing original clean address with place result from coordinates
          let geminiConfidence = 0
          let pinitConfidence = Math.round(pinitResult.geocoding.score * 100)

          if (placeResult?.formatted_address) {
            try {
              const comparisonString = `${original}|${cleanAddress}|${placeResult.formatted_address}`

              const confidenceResponse = await this.aiService.sendMessage(
                this.aiConfig.MODELS.CONFIDENCE,
                this.PROMPTS?.CONFIDENCE || '', // Fallback empty if not exists
                comparisonString,
                0
              )

              this.updateTelemetry(this.aiConfig.MODELS.CONFIDENCE, confidenceResponse.usageMetadata)

              // Use parseToonResponse from AiUtilsMixin
              const parsed = this.parseToonResponse(confidenceResponse.text)
              geminiConfidence = parsed?.confidence || 0

              console.log(
                `Confidence scores for Pinit coordinates (${context}): Gemini=${geminiConfidence}, Pinit=${pinitConfidence}`
              )
            } catch (error) {
              console.error(`Error calculating confidence for Pinit coordinates (${context}):`, error)
            }
          }

          return {
            success: true,
            byCoordinates: true,
            place: placeResult,
            gplaces: placeResult ? placeResult.formatted_address : 'FAIL',
            pinitResult: pinitResult,
            geminiConfidence: geminiConfidence,
            pinitConfidence: pinitConfidence
          }
        }

        const cleanAddr = pinitResult.clean_address
        const improvedAddress = `${cleanAddr.street || cleanAddr.street1 + '-' + cleanAddr.street2} ${
          cleanAddr.houseNumber
        }, ${cleanAddr.district}, ${cleanAddr.city}, ${cleanAddr.postalCode}, ${cleanAddr.state}, ${cleanAddr.country}`
        console.log(`Improved address from Pinit (${context}):`, improvedAddress)

        const encodedAddress = encodeURI(improvedAddress.replaceAll('#', '%23 ').replaceAll('+', '%2B'))
        const response = await this.$http.get(`${this.$appConfig.api.baseUrl}autocomplete?query=${encodedAddress}`)

        if (response.body && response.body.length > 0) {
          return {
            success: true,
            byCoordinates: false,
            improvedAddress: improvedAddress,
            autocompleteResults: response.body
          }
        }

        return { success: false }
      } catch (error) {
        console.error(`Error with Pinit validation (${context}):`, error)
        return { success: false, error }
      }
    },

    /**
     * Tries Pinit improvement with progress updates.
     * @param {string} original - Original address.
     * @param {string} cleanAddress - Cleaned address.
     * @param {string} context - 'single' or 'batch'.
     * @param {boolean} updateProgress - Whether to update progress bar.
     * @param {number} progressValue - Progress value to set.
     * @returns {Promise<Object>} Improvement result.
     */
    async tryPinitWithProgress(original, cleanAddress, context, updateProgress = false, progressValue = 65) {
      console.log('usePinit', this.usePinit)

      if (!this.usePinit) {
        return { success: false }
      }

      if (updateProgress && context === 'single') {
        this.singleProgress = progressValue
        this.singleProgressText = this.$gettext('Improving address with Pinit AI...')
      }

      const result = await this.tryPinitImprovement(original, cleanAddress, context)
      return result
    },

    /**
     * Applies the result from Pinit to the address object.
     * @param {Object} addressObj - Address object to update.
     * @param {Object} pinitResult - Result from Pinit.
     * @param {string} context - Context ('batch' or 'single').
     */
    async applyPinitResult(addressObj, pinitResult, context = 'batch') {
      if (pinitResult.byCoordinates) {
        addressObj.place = pinitResult.place
        addressObj.gplaces = pinitResult.gplaces
        addressObj.pinitImproved = true
        addressObj.pinitResult = pinitResult.pinitResult
        addressObj.orderedAutocomplete = { res: ['Pinit Coordinates (100%)'] }
        addressObj.engine = 'Pinit'

        if (pinitResult.geminiConfidence !== undefined) {
          addressObj.geminiConfidence = pinitResult.geminiConfidence
        }
        if (pinitResult.pinitConfidence !== undefined) {
          addressObj.pinitConfidence = pinitResult.pinitConfidence
        }

        // Set confidence to the lower score (more conservative approach)
        const gemini = pinitResult.geminiConfidence || 0
        const pinit = pinitResult.pinitConfidence || 0
        addressObj.confidence = Math.min(gemini, pinit)

        console.log(
          `Using lower confidence score for Pinit coordinates: ${addressObj.confidence} (Gemini: ${gemini}, Pinit: ${pinit})`
        )
      } else {
        await this.applyPinitImprovedAutocomplete(addressObj, pinitResult, context)
      }
    },

    /**
     * Applies autocomplete results from Pinit's improved address.
     * @param {Object} addressObj - Address object.
     * @param {Object} pinitResult - Pinit result containing improved address and autocomplete.
     * @param {string} context - Context.
     */
    async applyPinitImprovedAutocomplete(addressObj, pinitResult, context) {
      let newConfidences = await this.setAcConfidences(pinitResult.improvedAddress, pinitResult.autocompleteResults)

      newConfidences = this.normalizeConfidences(newConfidences)

      const sorted = newConfidences?.sort((a, b) => parseInt(b.confidence || 0) - parseInt(a.confidence || 0))

      addressObj.orderedAutocomplete = {
        res: sorted.map(x => `${x.main_text}${x.secondary_text ? ', ' + x.secondary_text : ''} (${x.confidence || 0}%)`)
      }

      const placeResult = await this.checkPlaces(sorted)
      addressObj.place = placeResult
      addressObj.gplaces = placeResult ? placeResult.formatted_address : 'FAIL'
      addressObj.pinitImproved = true
    },

    normalizeConfidences(confidences) {
      return confidences.autocomplete || confidences
    },

    // ------------------------------------------------------------------------
    // MAIN PROCESSING LOGIC
    // ------------------------------------------------------------------------

    /**
     * Processes single address with progress bar updates.
     * @param {string} addressString - The raw address string input.
     * @returns {Promise<Object>} Processed address object.
     */
    async processSingleAddressWithProgress(addressString) {
      if (!addressString || typeof addressString !== 'string') {
        throw new Error('Address string is required and must be a string')
      }

      const addressObj = this.createAddressObject(addressString)

      try {
        console.log('Processing single address:', addressString)

        this.singleProgress = 25
        this.singleProgressText = this.$gettext('Cleaning and standardizing address...')

        // Call CLEAN logic
        await this.processSingleAddressClean(addressObj)
        console.log('Clean result:', addressObj.clean)

        if (addressObj.clean !== 'ERROR') {
          this.singleProgress = 50
          this.singleProgressText = this.$gettext('Searching in Google Places...')

          // Call AUTOCOMPLETE logic
          await this.processSingleAddressAutocomplete(addressObj, true)
          console.log('Autocomplete result:', addressObj.autocomplete)
          console.log('Google Places result:', addressObj.gplaces)

          if (addressObj.gplaces !== 'FAIL') {
            this.singleProgress = 80
            this.singleProgressText = this.$gettext('Calculating confidence score...')

            // Call CONFIDENCE logic
            await this.processSingleAddressConfidence(addressObj)
            console.log('Final confidence:', addressObj.confidence)
          }
        }

        return addressObj
      } catch (error) {
        console.error('Error processing single address:', error)
        const failedObj = this.createAddressObject(addressString)
        failedObj.clean = 'ERROR'
        failedObj.error = error.message
        this.markAddressAsFailed(failedObj)
        return failedObj
      }
    },

    /**
     * Cleaning step using AI.
     * @param {Object} addressObj - Address object to process.
     */
    async processSingleAddressClean(addressObj) {
      try {
        const response = await this.aiService.sendMessage(
          this.aiConfig.MODELS.CLEAN,
          this.PROMPTS?.CLEAN || '',
          addressObj.original,
          0
        )

        this.updateTelemetry(this.aiConfig.MODELS.CLEAN, response.usageMetadata)

        const parsed = this.parseToonResponse(response.text)

        if (parsed) {
          if (!parsed.data && parsed.address) {
            await this.processSingleAddressClean(addressObj)
            return
          } else if ((parsed.error && parsed.error.length !== 0) || parsed.data.length === 0) {
            addressObj.clean = 'ERROR'
            addressObj.cleanOptions = []
            addressObj.error = parsed.error
          } else {
            const sortedOptions = parsed.data.sort((a, b) => b.confidence - a.confidence)
            addressObj.clean = sortedOptions[0].guess
            addressObj.cleanOptions = sortedOptions
          }
        } else {
          addressObj.clean = 'ERROR'
          addressObj.cleanOptions = []
        }
      } catch (error) {
        console.error('Error cleaning single address:', error)
        addressObj.clean = 'ERROR'
        addressObj.cleanOptions = []
        addressObj.error = error
        throw error
      }
    },

    /**
     * Autocomplete step using Google Places and AI reranking.
     * @param {Object} addressObj - Address object.
     * @param {boolean} updateProgress - Whether to update UI progress.
     */
    async processSingleAddressAutocomplete(addressObj, updateProgress = false) {
      if (addressObj.clean === 'ERROR') {
        this.markAddressAsFailed(addressObj)
        return
      }

      try {
        const cleanOptions = addressObj.cleanOptions || [{ guess: addressObj.clean, confidence: 100 }]
        const allAutocompleteResults = []

        for (const cleanOption of cleanOptions) {
          const encodedAddress = this.encodeAddressForAPI(cleanOption.guess)
          const response = await this.$http.get(`${this.$appConfig.api.baseUrl}autocomplete?query=${encodedAddress}`)
          allAutocompleteResults.push(...response.body)
        }

        if (allAutocompleteResults.length === 0) {
          const bestCleanAddress = cleanOptions[0].guess
          const result = await this.tryPinitWithProgress(
            addressObj.original,
            bestCleanAddress,
            'single',
            updateProgress,
            60
          )

          if (result.success) {
            await this.applyPinitResult(addressObj, result, 'single')
            return
          }

          this.markAddressAsFailed(addressObj)
          return
        }

        const results = allAutocompleteResults.map(
          x => x.main_text + (x.secondary_text !== '' ? ', ' + x.secondary_text : '')
        )

        addressObj.autocomplete = { res: results }
        addressObj.orderedAutocomplete = { res: results }

        if (allAutocompleteResults.length === 1) {
          const placeResult = await this.checkPlaces(allAutocompleteResults)

          if (!placeResult || !placeResult.formatted_address || !placeResult.is_valid) {
            const bestCleanAddress = cleanOptions[0].guess
            const result = await this.tryPinitWithProgress(
              addressObj.original,
              bestCleanAddress,
              'single',
              updateProgress,
              65
            )

            if (result.success) {
              await this.applyPinitResult(addressObj, result, 'single')
              return
            }

            addressObj.place = null
            addressObj.gplaces = 'FAIL'
            return
          }

          addressObj.place = placeResult
          addressObj.gplaces = placeResult.formatted_address
        } else {
          const bestCleanAddress = cleanOptions[0].guess
          let confidences = await this.setAcConfidences(bestCleanAddress, allAutocompleteResults)
          confidences = this.normalizeConfidences(confidences)

          const sortedConfidences = confidences?.sort(
            (a, b) => parseInt(b.confidence || 0) - parseInt(a.confidence || 0)
          )

          const maxConfidence = sortedConfidences.length > 0 ? parseInt(sortedConfidences[0].confidence || 0) : 0

          if (maxConfidence < this.aiConfig.thresholds.pinitActivation) {
            const result = await this.tryPinitWithProgress(
              addressObj.original,
              bestCleanAddress,
              'single',
              updateProgress,
              65
            )

            if (result.success) {
              await this.applyPinitResult(addressObj, result, 'single')
              return
            }
          }

          addressObj.orderedAutocomplete = {
            res: sortedConfidences.map(
              x => `${x.main_text}${x.secondary_text !== '' ? ', ' + x.secondary_text : ''} (${x.confidence || 0}%)`
            )
          }

          const placeResult = await this.checkPlaces(sortedConfidences)
          addressObj.place = placeResult
          addressObj.gplaces = placeResult ? placeResult.formatted_address : 'FAIL'
        }
      } catch (error) {
        console.error('Error in single address autocomplete:', error)
        this.markAddressAsFailed(addressObj)
        throw error
      }
    },

    /**
     * Assigns confidence scores to autocomplete results using AI.
     * @param {string} cleanAddress - The cleaned address for comparison.
     * @param {Array} addresses - List of autocomplete suggestions.
     * @returns {Promise<Array>} List of addresses with confidence scores.
     */
    async setAcConfidences(cleanAddress, addresses) {
      // Remove duplicates before processing
      const uniqueAddresses = this.removeDuplicateAutocomplete(addresses)
      try {
        const message = JSON.stringify({
          clean: cleanAddress,
          autocomplete: uniqueAddresses
        })
        const response = await this.aiService.sendMessage(
          this.aiConfig.MODELS.CONFIDENCE,
          this.PROMPTS?.AUTOCOMPLETE_CONFIDENCE || '',
          message,
          0
        )

        this.updateTelemetry(this.aiConfig.MODELS.CONFIDENCE, response.usageMetadata)
        const parsed = this.parseToonResponse(response.text)
        return parsed
      } catch (error) {
        console.error('Error setting AC confidence:', error)
        return uniqueAddresses
      }
    },

    /**
     * Removes duplicate places from autocomplete results.
     * @param {Array} addresses - List of addresses.
     * @returns {Array} List of unique addresses.
     */
    removeDuplicateAutocomplete(addresses) {
      const seen = new Set()
      return addresses.filter(item => {
        const key = item.place_id || `${item.main_text}|${item.secondary_text}`
        if (seen.has(key)) {
          return false
        }
        seen.add(key)
        return true
      })
    },

    /**
     * Calculates the final confidence score using 3-way validation.
     * @param {Object} addressObj - The address object.
     */
    async processSingleAddressConfidence(addressObj) {
      if (addressObj.clean === 'ERROR' || addressObj.gplaces === 'FAIL') {
        addressObj.confidence = 0
        addressObj.engine = 'Error'
        return
      }

      if (addressObj.pinitImproved && addressObj.pinitResult?.geocoding?.score) {
        const pinitConfidence = Math.round(addressObj.pinitResult.geocoding.score * 100)

        // If we already have both scores, use the minimum
        if (addressObj.geminiConfidence !== undefined && addressObj.pinitConfidence !== undefined) {
          addressObj.confidence = Math.min(addressObj.geminiConfidence, addressObj.pinitConfidence)
          addressObj.engine = 'Pinit'
          console.log(
            `Using lower confidence score for Pinit (single): ${addressObj.confidence} (Gemini: ${addressObj.geminiConfidence}, Pinit: ${addressObj.pinitConfidence})`
          )
          return
        }

        // If we don't have Gemini confidence, calculate it first
        try {
          const addressString = `${addressObj.original}|${addressObj.clean}|${addressObj.gplaces}`
          const response = await this.aiService.sendMessage(
            this.aiConfig.MODELS.FINAL_CONFIDENCE,
            this.PROMPTS?.CONFIDENCE || '', // Fallback empty
            addressString,
            0
          )

          const geminiConfidence = this.extractConfidenceScore(response.text)

          // Now use MIN of both scores
          addressObj.confidence = Math.min(geminiConfidence, pinitConfidence)
          addressObj.geminiConfidence = geminiConfidence
          addressObj.pinitConfidence = pinitConfidence
          addressObj.engine = 'Pinit'
          console.log(
            `Using lower confidence score for Pinit (single): ${addressObj.confidence} (Gemini: ${geminiConfidence}, Pinit: ${pinitConfidence})`
          )
          return
        } catch (error) {
          console.error('Error calculating Gemini confidence for Pinit result:', error)
          // Fallback to Pinit score only if Gemini fails
          addressObj.confidence = pinitConfidence
          addressObj.engine = 'Pinit'
          return
        }
      }

      try {
        const addressString = `${addressObj.original}|${addressObj.clean}|${addressObj.gplaces}`
        const response = await this.aiService.sendMessage(
          this.aiConfig.MODELS.FINAL_CONFIDENCE,
          this.PROMPTS?.CONFIDENCE || '',
          addressString,
          0
        )

        this.updateTelemetry(this.aiConfig.MODELS.FINAL_CONFIDENCE, response.usageMetadata)

        // Use extracted confidence score
        const confidenceScore = this.extractConfidenceScore(response.text)

        addressObj.confidence = confidenceScore
        addressObj.engine = 'Gemini'

        // If confidence is 70 or less and Pinit is available, try improvement
        console.log('this.usePinit', this.usePinit)

        if (confidenceScore <= this.aiConfig.thresholds.pinitLastResource && this.usePinit) {
          const bestCleanAddress = addressObj.cleanOptions?.[0]?.guess || addressObj.clean
          console.log(`Low confidence score (${confidenceScore}), trying Pinit improvement...`)

          const result = await this.tryPinitImprovement(addressObj.original, bestCleanAddress, 'single')

          if (result.success) {
            await this.applyPinitResult(addressObj, result, 'single')
            console.log('Address improved with Pinit after low confidence score')
          }
        }
      } catch (error) {
        console.error('Error setting single address confidence:', error)
        addressObj.confidence = 0
        addressObj.engine = 'Error'
      }
    }
  }
}
